import { join } from "path";

import { ConfigSubdomain } from "@/configuration/tenant-ids";
import { RENAMED_CONFIG_FOLDERS } from "@/shared/group-configs/renamed-subdomains";

export const GROUPS_CONFIGURATION_DIR = join(process.cwd(), "configuration", "groups");
export const DEFAULT_CONFIGURATION_DIR = join(process.cwd(), "configuration", "default");
export const SANDBOX_SUBDOMAIN_SUFFIX = "-test";

const ROUTING_SUBDOMAIN_BY_SHARED_FOLDER: Record<string, ConfigSubdomain> = {
  demo: ConfigSubdomain.VALERIE,
};

// Live + sandbox old/new `group.subdomain` pairs, so host routing, Stytch magic-link slugs, and
// patient scheduling links resolve under either name during the rollout.
const RENAMED_GROUP_SUBDOMAINS: readonly (readonly [string, string])[] = Object.entries(RENAMED_CONFIG_FOLDERS).flatMap(
  ([before, after]): (readonly [string, string])[] => [
    [before, after],
    [`${before}${SANDBOX_SUBDOMAIN_SUFFIX}`, `${after}${SANDBOX_SUBDOMAIN_SUFFIX}`],
  ]
);

export const UPPERLINE_CONFIG_SUBDOMAINS = [
  ConfigSubdomain.UPPERLINE_FLORIDA,
  ConfigSubdomain.UPPERLINE_GEORGIA,
  ConfigSubdomain.UPPERLINE_INDIANA,
  ConfigSubdomain.UPPERLINE_TENNESSEE,
] as const;

export type UpperlineConfigSubdomain = (typeof UPPERLINE_CONFIG_SUBDOMAINS)[number];

const UPPERLINE_CONFIG_SUBDOMAIN_SET = new Set<string>(UPPERLINE_CONFIG_SUBDOMAINS);

export function isUpperlineConfigSubdomain(subdomain: string): subdomain is UpperlineConfigSubdomain {
  return UPPERLINE_CONFIG_SUBDOMAIN_SET.has(subdomain);
}

export function mapSubdomains<S extends string, T>(subdomains: readonly S[], value: T): Record<S, T> {
  return Object.fromEntries(subdomains.map((subdomain) => [subdomain, value])) as Record<S, T>;
}

// Not exported on purpose: it strips the sandbox suffix but does not apply tenant renames, so it is
// never the right helper for a config path. Use `getConfigFolderForSubdomain`.
function getSharedConfigFolderFromSubdomain(subdomain: string): string {
  if (subdomain.endsWith(SANDBOX_SUBDOMAIN_SUFFIX)) {
    return subdomain.slice(0, -SANDBOX_SUBDOMAIN_SUFFIX.length);
  }

  return subdomain;
}

export function isSandboxSubdomain(subdomain: string): boolean {
  return subdomain.endsWith(SANDBOX_SUBDOMAIN_SUFFIX);
}

/**
 * The `configuration/groups/` folder backing a tenant subdomain: sandbox suffix stripped, then any
 * in-place rename applied. Use this (not `getSharedConfigFolderFromSubdomain`) to build config file
 * paths, so a renamed tenant keeps loading its YAML from either subdomain.
 */
export function getConfigFolderForSubdomain(subdomain: string): string {
  const folder = getSharedConfigFolderFromSubdomain(subdomain);

  return RENAMED_CONFIG_FOLDERS[folder] ?? folder;
}

export function getGroupConfigSubdomain(group: { subdomain: string }): string {
  const folder = getConfigFolderForSubdomain(group.subdomain);

  return ROUTING_SUBDOMAIN_BY_SHARED_FOLDER[folder] ?? folder;
}

/** The other subdomain a renamed tenant's group row may still be stored under, if any. */
export function getRenamedSubdomainAlias(subdomain: string): string | null {
  const pair = RENAMED_GROUP_SUBDOMAINS.find(([before, after]) => before === subdomain || after === subdomain);

  if (!pair) {
    return null;
  }

  return pair[0] === subdomain ? pair[1] : pair[0];
}

/** Canonical tenant subdomain, so a renamed tenant's old and new hosts compare equal. */
export function canonicalTenantSubdomain(subdomain: string): string {
  return RENAMED_GROUP_SUBDOMAINS.find(([before]) => before === subdomain)?.[1] ?? subdomain;
}
